/**
 * Global constants: rendering scale, palette, and physics tuning.
 * No magic numbers in the game loop — everything tunable lives here or in `game/data/`.
 */

/** Base design resolution. We author at 16:9 landscape and FIT-scale to the device. */
export const GAME_WIDTH = 1280;
export const GAME_HEIGHT = 720;

/** Fixed simulation step (seconds) for deterministic tap physics, decoupled from render. */
export const SIM_STEP = 1 / 120;
/** Cap on accumulated sim steps per frame to avoid spiral-of-death on slow devices. */
export const MAX_SIM_STEPS_PER_FRAME = 8;

/** Cartoon-3D-render leaning palette: deep night-blue stage, warm energetic accents. */
export const PALETTE = {
  bgTop: 0x141d3b,
  bgBottom: 0x0b1020,
  track: 0x2a3358,
  trackLine: 0x3d4a7a,
  laneAccent: 0xff7a59,
  sky: 0x1b2a5e,
  sun: 0xffd24a,
  ink: 0x0b1020,
  white: 0xf4f7ff,
  good: 0x4ade80,
  warn: 0xffd24a,
  bad: 0xff5d6c,
  power: 0xffb020,
  powerHot: 0xff5d6c,
  cadence: 0x59d2ff,
} as const;

/** CSS color strings mirror of PALETTE for DOM/text usage. */
export const CSS = {
  white: '#f4f7ff',
  ink: '#0b1020',
  accent: '#ff7a59',
  cadence: '#59d2ff',
} as const;

/**
 * Sprint physics. The model: alternating left/right taps inject impulse; velocity
 * bleeds off via drag every sim step. Rewarding *rhythm* (clean alternation near a
 * target cadence) over mashing one button is what makes the genre feel skillful.
 */
export const SPRINT = {
  raceMeters: 100,
  /** World pixels per in-game meter (controls how fast the world appears to scroll). */
  pixelsPerMeter: 42,
  /** Impulse (m/s) added by a single perfectly-alternated tap at base stats. */
  tapImpulse: 1.05,
  /** Velocity multiplier applied each sim step (drag). Lower = stops faster. */
  dragPerStep: 0.992,
  /** Hard ceiling on velocity (m/s) before stat scaling. */
  maxSpeed: 12.5,
  /** Tapping the *same* side twice in a row is inefficient — impulse is scaled by this. */
  sameSidePenalty: 0.35,
  /**
   * Ideal cadence band (taps/second). Hitting it grants a flow bonus; spamming far
   * above it overspins and is penalised, so the player must find a sustainable rhythm.
   */
  idealCadenceMin: 4.5,
  idealCadenceMax: 7.5,
  flowBonus: 1.35,
  overspinPenalty: 0.55,
  /** Cadence above this (taps/s) counts as overspin. */
  overspinThreshold: 9.5,
  /** Seconds of inactivity before cadence is considered broken. */
  cadenceWindow: 0.9,
  /** False-start window: tapping before the gun disqualifies the start (soft penalty). */
  falseStartGrace: 0.0,
} as const;

/** Haptic patterns (ms) for navigator.vibrate. Kept short — long buzzes feel cheap. */
export const HAPTICS = {
  tap: 8,
  goodTap: 14,
  countdownTick: 20,
  go: [30, 40, 60] as number[],
  finish: [60, 50, 120] as number[],
} as const;

export const STORAGE_KEY = 'zoolympics:save';
export const SAVE_VERSION = 2;

/**
 * Google OAuth Web client ID for optional cloud save (cross-device progress). Set at build time
 * via VITE_GOOGLE_CLIENT_ID (e.g. in .env.local). Empty = login UI stays hidden, game is fully
 * playable as a guest. The matching value must be set as GOOGLE_CLIENT_ID on the API server too.
 */
export const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ?? '';

/**
 * Base URL for the online API (leaderboard, arena, accounts, cloud save). Same-origin `/api` on our
 * own hosting; an ABSOLUTE URL for the CrazyGames build (the game runs on crazygames.com there, so a
 * relative `/api` would hit the wrong host). Our API sends `Access-Control-Allow-Origin: *`, so the
 * cross-origin calls are allowed. Set via VITE_API_BASE at build time.
 */
export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) || '/api';

/** Absolute URL to the privacy/terms page (works on any host, incl. the CrazyGames iframe). */
export const PRIVACY_URL = 'https://zoolympics.vibecanyon.com/privacy.html';
